/**
 * Internal helper for rendering the leading element of an entity badge.
 *
 * Encapsulates the avatar / initials / type-icon decision so the badge
 * components stay focused on layout and click behavior. Not exported
 * from the package barrel.
 *
 * Fallback rule:
 *   - person types (ahoy_contact, ahoy_user) → image when `avatarUrl`,
 *     otherwise initials derived from `displayName`
 *   - other types → entity-type icon in a rounded-square container, EXCEPT the
 *     `record` variant when the type is image-backed (`IMAGE_AVATAR_TYPES` —
 *     among non-persons, only `ahoy_company`), where initials are legible at
 *     24px and are what the CRM list's primary column has always shown. Icon-
 *     only types (deal, stage, custom, …) keep the glyph even at `record` size,
 *     matching `CrmObjectAvatar` exactly (see `INITIALS_FALLBACK_VARIANTS`).
 */

import { useCallback, useEffect, useState } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { IMAGE_AVATAR_TYPES, PERSON_TYPES } from '@/lib/crm/core/entityTypes';
import { cx } from '@/utils/cx';

import { createFaviconLoadGuard, getAvatarInitials } from '../../utils/avatar';
import { getEntityTypeIconComponent } from '../../utils/icons/EntityTypeIcon';
import type { EntityBadgeStatus, EntityBadgeStatusColor } from './types';

/** Background colour per status, mapped to dark-mode-aware utility tokens. */
const STATUS_DOT_BG_CLASSES: Record<EntityBadgeStatusColor, string> = {
  success: 'bg-utility-success-500',
  warning: 'bg-utility-warning-500',
  error: 'bg-utility-error-500',
  neutral: 'bg-utility-gray-300',
};

/**
 * Status-dot positioning. The dot anchors to the bottom-right of the
 * avatar (Untitled-UI online-indicator convention) with a small ring
 * for contrast against the pill background.
 */
const STATUS_DOT_BASE_CLASSES =
  'absolute -bottom-px -right-px rounded-full ring-1 ring-white dark:ring-neutral-800';

/** Dot size per badge variant (matches the avatar size below). */
const STATUS_DOT_SIZE_BY_VARIANT = {
  default: 'size-1.5',
  inline: 'size-1.5',
  xs: 'size-1.5',
  // Scales with the larger avatar; at size-1.5 the dot reads as a speck
  // against a 24px circle rather than a status.
  record: 'size-2',
} as const;

/** Tailwind size classes for the icon container per badge variant. */
const ICON_CONTAINER_CLASSES = {
  default: 'size-4',
  inline: 'size-4',
  xs: 'size-3.5',
  record: 'size-6',
} as const;

/** Tailwind size classes for the icon glyph itself per badge variant. */
const ICON_GLYPH_CLASSES = {
  default: 'size-3',
  // 14px to match the legacy `EntityElement` (Plate) chip — the inline
  // variant is intended to be a drop-in replacement for that element.
  inline: 'size-3.5',
  xs: 'size-2.5',
  record: 'size-4',
} as const;

/**
 * Avatar size per badge variant. The base `Avatar` only supports its
 * fixed t-shirt sizes; `xxs` (16px) is the smallest and works for every
 * variant we currently need. Kept as a map so future variants can scale.
 *
 * `record` is the one that scales: `xs` is 24px, matching what the CRM list's
 * primary column has always drawn via `CrmObjectAvatar size='xs'`. The number
 * is copied from there deliberately — the variant exists so those two stop
 * being separate implementations of the same picture.
 */
const AVATAR_SIZE_BY_VARIANT = {
  default: 'xxs',
  inline: 'xxs',
  xs: 'xxs',
  record: 'xs',
} as const;

/**
 * Variants whose non-person fallback *may* be initials rather than the
 * entity-type glyph, when no image resolves.
 *
 * This is a legibility call, not a style one. Two letters need roughly 24px to
 * read; below that they turn into grey mush and a shape ("this is a company")
 * carries more than an unreadable "GL". So the compact variants keep the glyph
 * and `record` — the only one drawn at 24px — shows initials, which is also
 * what `CrmObjectAvatar` has always done in the CRM list's primary column. The
 * two have to agree there: `record` exists precisely so that column and a
 * report's grouped-entity column stop being separate implementations.
 *
 * Membership here is necessary but NOT sufficient: initials are only shown for
 * image-backed types (`IMAGE_AVATAR_TYPES`). Icon-only types (deal, stage,
 * custom, …) keep the glyph even at `record` size, because a two-letter
 * abbreviation of "Acme–Globex deal" is more ambiguous than the deal glyph —
 * again matching `CrmObjectAvatar`, which routes those through `IconAvatar`.
 */
const INITIALS_FALLBACK_VARIANTS: ReadonlySet<string> = new Set(['record']);

/** Initials type scale per variant. Only meaningful for the set above. */
const INITIALS_CLASSES = 'text-[10px] font-semibold leading-none';

export type EntityBadgeAvatarVariant = 'default' | 'inline' | 'xs' | 'record';

interface EntityBadgeAvatarProps {
  entityType: string;
  displayName: string;
  /** When omitted (and entity is a person type), the avatar shows initials. */
  avatarUrl?: string;
  /**
   * When `false`, render only a small leading icon — no rounded
   * container, no initials. This is the historical CrmObjectBadge look.
   */
  avatar: boolean;
  variant: EntityBadgeAvatarVariant;
  /**
   * Optional status indicator overlaid on the avatar (bottom-right). Only
   * rendered when `avatar` is true — the leading-icon mode has no
   * sensible anchor point.
   */
  status?: EntityBadgeStatus;
}

/** Internal helper: render the status dot overlay (no-op when status undefined). */
function StatusDot({
  status,
  variant,
}: {
  status: EntityBadgeStatus | undefined;
  variant: EntityBadgeAvatarVariant;
}) {
  if (!status) {
    return null;
  }
  return (
    <span
      role='status'
      aria-label={status.label}
      title={status.label}
      className={cx(
        STATUS_DOT_BASE_CLASSES,
        STATUS_DOT_SIZE_BY_VARIANT[variant],
        STATUS_DOT_BG_CLASSES[status.color]
      )}
    />
  );
}

/**
 * Internal helper: render a badge image that falls through to `fallback`
 * when the image fails to load or resolves to the tiny default favicon
 * globe (see `createFaviconLoadGuard`).
 */
function BadgeAvatarImage({
  src,
  imgClassName,
  fallback,
}: {
  src: string;
  imgClassName: string;
  fallback: React.ReactElement;
}): React.ReactElement {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  const handleReject = useCallback(() => setFailed(true), []);
  if (failed) {
    return fallback;
  }
  return (
    <img
      src={src}
      alt=''
      aria-hidden
      className={imgClassName}
      onLoad={
        src.includes('/s2/favicons?')
          ? createFaviconLoadGuard(handleReject)
          : undefined
      }
      onError={handleReject}
    />
  );
}

/**
 * Render the leading element for an entity badge.
 */
export function EntityBadgeAvatar({
  entityType,
  displayName,
  avatarUrl,
  avatar,
  variant,
  status,
}: EntityBadgeAvatarProps): React.ReactElement {
  const Icon = getEntityTypeIconComponent(entityType);
  const isPerson = PERSON_TYPES.has(entityType);

  // `avatar={false}` — minimal leading element. For non-person entities
  // (most notably companies, which carry a favicon URL via the domain),
  // render the brand image when available so badges in dense cell contexts
  // show recognizable logos instead of a generic building/object icon.
  // Person types stay on the silhouette glyph here — surfacing photos in
  // dense cells is broader than the current ask. Status dots are
  // intentionally suppressed in this mode: no sensible anchor point on a
  // bare icon and overlapping produces a muddy visual.
  if (!avatar) {
    if (avatarUrl && !isPerson) {
      return (
        <BadgeAvatarImage
          src={avatarUrl}
          imgClassName={cx(
            ICON_GLYPH_CLASSES[variant],
            'shrink-0 rounded-sm object-contain'
          )}
          fallback={
            <Icon
              className={cx(
                ICON_GLYPH_CLASSES[variant],
                'shrink-0 text-[var(--color-fg-quaternary)]'
              )}
              aria-hidden
            />
          }
        />
      );
    }
    return (
      <Icon
        className={cx(
          ICON_GLYPH_CLASSES[variant],
          'shrink-0 text-[var(--color-fg-quaternary)]'
        )}
        aria-hidden
      />
    );
  }

  // `avatar={true}` for person types — image when available, initials
  // otherwise. Status dot anchors to the bottom-right of the avatar.
  if (isPerson) {
    return (
      <span className='relative inline-flex shrink-0'>
        <Avatar
          size={AVATAR_SIZE_BY_VARIANT[variant]}
          src={avatarUrl ?? undefined}
          alt={displayName}
          initials={getAvatarInitials(displayName)}
        />
        <StatusDot status={status} variant={variant} />
      </span>
    );
  }

  // `avatar={true}` for non-person types — favicon-or-icon in a
  // rounded-square container. Square (rounded-sm) instead of full circle
  // so it reads as an "object" rather than a "person" at a glance.
  // When the entity carries an `avatarUrl` (e.g. company favicon resolved
  // from its domain), render the brand image inside the container so the
  // surface shows recognizable identity, falling back to the generic type
  // icon when the image is unavailable.
  // Shared by both the no-image path and the image-failed path, so a broken
  // favicon lands on the same thing an absent one does.
  // Initials only for image-backed types (among non-persons, that's company);
  // icon-only types keep the glyph so `record` matches `CrmObjectAvatar` exactly.
  const nonPersonFallback =
    INITIALS_FALLBACK_VARIANTS.has(variant) &&
    IMAGE_AVATAR_TYPES.has(entityType) ? (
      <span
        className={cx(INITIALS_CLASSES, 'text-[var(--color-text-secondary)]')}
      >
        {getAvatarInitials(displayName)}
      </span>
    ) : (
      <Icon className={ICON_GLYPH_CLASSES[variant]} />
    );

  return (
    <span className='relative inline-flex shrink-0'>
      <span
        className={cx(
          'inline-flex shrink-0 items-center justify-center rounded-sm overflow-hidden',
          'bg-[var(--color-bg-tertiary)] text-[var(--color-fg-quaternary)]',
          ICON_CONTAINER_CLASSES[variant]
        )}
        aria-hidden
      >
        {avatarUrl ? (
          <BadgeAvatarImage
            src={avatarUrl}
            imgClassName='size-full object-contain'
            fallback={nonPersonFallback}
          />
        ) : (
          nonPersonFallback
        )}
      </span>
      <StatusDot status={status} variant={variant} />
    </span>
  );
}
